import { ArrowRight } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useNavigate } from "react-router-dom";

const UNIVERS = [
  {
    num: "01",
    title: "Réinventer l'existant.",
    tag: "Rénovation",
    text: "Appartement, maison, rénovation complète ou transformation de plusieurs pièces : nous vous aidons à définir vos besoins, préparer votre projet et organiser sa réalisation.",
    text2: "De la première estimation jusqu'aux travaux, vous avancez avec un accompagnement adapté à votre projet et à votre niveau d'autonomie.",
    img: "https://images.unsplash.com/photo-1705321963943-de94bb3f0dd3?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: null,
  },
  {
    num: "02",
    title: "Un intérieur pensé autour de votre façon de vivre.",
    tag: "Aménagement intérieur",
    text: "Repenser les volumes, ouvrir une cuisine, créer une suite parentale, optimiser les rangements ou simplement rendre votre intérieur plus agréable au quotidien.",
    text2: "CozyHome vous accompagne pour transformer vos idées en un projet cohérent, esthétique et réalisable.",
    img: "https://images.unsplash.com/photo-1610177534644-34d881503b83?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: null,
  },
  {
    num: "03",
    title: "Plus d'espace, sans forcément changer de maison.",
    tag: "Extension & transformation",
    text: "Une pièce supplémentaire, une extension, une surélévation ou une transformation importante de l'existant : nous vous aidons à étudier les possibilités et à préparer les différentes étapes du projet.",
    text2: "Lorsque le projet nécessite des plans, des démarches administratives ou des compétences spécifiques, les prestations adaptées peuvent être intégrées au parcours.",
    img: "https://images.unsplash.com/photo-1753893558279-8beaad18dd96?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: null,
  },
  {
    num: "04",
    title: "Construire une maison qui vous ressemble.",
    tag: "Construction",
    text: "Partez d'une idée, d'un terrain ou de l'un de nos modèles et construisez progressivement votre projet.",
    text2: "Volumes, organisation intérieure, style, besoins de la famille, budget et contraintes du terrain : les modèles CozyHome sont des points de départ, pas des cases dans lesquelles votre projet doit entrer.",
    img: "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: { label: "Découvrir nos modèles", to: "/modeles", testid: "service-row-cta-models" },
  },
];

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <PageShell>
      <PageHero
        testid="page-services-hero"
        label="Nos services"
        title={<>Votre projet, accompagné de l'idée <span className="italic text-sage">jusqu'aux travaux.</span></>}
        sub="Rénover, agrandir, aménager ou construire demande de faire les bons choix au bon moment. CozyHome vous aide à structurer votre projet, à trouver les professionnels adaptés et à garder une vision claire de chaque étape."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col gap-20 sm:gap-28">
            {UNIVERS.map((u, i) => (
              <Reveal key={u.num}>
                <div
                  data-testid={`service-row-${i}`}
                  className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-16 ${
                    i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
                  }`}
                >
                  <div className="overflow-hidden rounded-3xl">
                    <img
                      src={u.img}
                      alt={u.tag}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : "auto"}
                      decoding="async"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac">{u.tag}</p>
                    <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                      {u.title}
                    </h2>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">{u.text}</p>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-stone-500 sm:text-base">{u.text2}</p>
                    <button
                      data-testid={u.cta ? u.cta.testid : `service-row-cta-${i}`}
                      onClick={() => navigate(u.cta ? u.cta.to : "/estimer")}
                      className="group mt-7 flex w-fit items-center gap-2 text-sm font-bold text-cognac transition-colors duration-300 hover:text-navy"
                    >
                      {u.cta ? u.cta.label : "Parlez-nous de votre projet"}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div data-testid="services-band" className="mt-24 rounded-3xl bg-cream p-8 text-center sm:p-14">
              <p className="font-display text-2xl font-medium leading-snug text-navy sm:text-3xl">
                Un projet complet, <span className="italic text-cognac">un accompagnement plus simple.</span>
              </p>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                Avec CozyHome, vous ne devez pas assembler seul toutes les pièces du
                puzzle. Votre projet est structuré étape par étape : définition des
                besoins, préparation, professionnels, devis, organisation et, selon
                l'accompagnement choisi, suivi des travaux.
              </p>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                Votre espace CozyHome vous permet également de retrouver les
                informations utiles de votre projet au même endroit.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
